import { FC, ReactNode, LazyExoticComponent, ComponentType, useEffect, useState } from "react";
import { Bullseye, EmptyState, EmptyStateBody, Spinner } from "@patternfly/react-core";
import { ExclamationTriangleIcon } from "@patternfly/react-icons";
import { isEmpty } from "lodash";
import {
  getProjectTypeData,
  ProjectContainerTypes,
  ProjectDrupalKeys,
  ProjectKeys,
  ProjectTypes,
  TGetProjectTypeDataReturnType
} from "../../constants/project";
import ArchivedProjectError from "../shared/ArchivedProjectError/ArchivedProjectError";
import Env from "../../utils/env";
import { IsOperatorBundleProject } from "../../utils/projectUtils";
import { useParams } from "react-router";
import { CWRouteParams } from "../../constants/router";
import { IProjectDetails } from "../../interfaces/project";
import { InitialPaginationValue } from "../../constants/pagination";
import { RHCErrorPage } from '@rhc-shared-components/error-page';
import { useProductDetails } from "../../queries/getProductDetails";
import { useProjectDetails } from "../../queries/getProjectDetails";
import { useProjectChecklist } from "../../queries/getProjectChecklist";
import { useProjectRHCertStatus } from "../../queries/getProjectRHCertStatus";
import { usePartnerInfo } from "../../queries/getPartnerInfo";
import { useAttachedProducts } from "../../queries/getAttachedProducts";

const DeprecatedTypeRedirect: FC = () => {
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    if (window.location.port === '1337') return;
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          window.location.href = `${Env.productListingUrl}/components`;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Bullseye>
      <EmptyState
        titleText="This component type is no longer supported."
        headingLevel="h2"
        icon={ExclamationTriangleIcon}
        status="warning"
      >
        <EmptyStateBody>
          Redirecting to components list in {countdown} second{countdown !== 1 ? 's' : ''}...
        </EmptyStateBody>
      </EmptyState>
    </Bullseye>
  );
};

export interface ICertificationWorkflowWrapperProps {
  children?: JSX.Element | JSX.Element[] | LazyExoticComponent<ComponentType<any>> | ReactNode;
}

const CertWorkflowAppWrapper: FC<ICertificationWorkflowWrapperProps> = (props: ICertificationWorkflowWrapperProps) => {
  const { id, productId: attachedProductId } = useParams<CWRouteParams>();
  const {
    data: projectDetails,
    isLoading: projectDetailsFetching,
    error: responseError
  } = useProjectDetails(id);

  const isDeprecatedType = !isEmpty(projectDetails) && (
    projectDetails[ProjectKeys.type] === ProjectTypes.Middleware ||
    projectDetails[ProjectKeys.type] === ProjectTypes.openShiftVirtualization ||
    (projectDetails[ProjectKeys.type] === ProjectTypes.openshiftCNF && projectDetails[ProjectKeys.certificationLevel] === "Vendor validated")
  );
  const {
    isLoading: isProductInfoFetching,
    error: productIdError
  } = useProductDetails(attachedProductId, attachedProductId !== Env.productIdStatic && !isDeprecatedType);
  const { isLoading: isProjectChecklistLoading, error: projectCheckListError } = useProjectChecklist(id, !isEmpty(projectDetails) && !isDeprecatedType);
  const checkIsRHCertEnabled = (projectDetails: IProjectDetails): boolean => {
    let isEnabled: boolean = false;
    const RHCertProjectType: TGetProjectTypeDataReturnType[] = [
      ProjectTypes.openshiftCNF, ProjectTypes.openStackInfraContainer,
      ProjectTypes.openStackInfraNonContainer, ProjectTypes.openStackVnf,
      ProjectTypes.RHEL, "RHEL-NonContainer", ProjectContainerTypes.OPERATOR_BUNDLE_IMAGE,
      ProjectContainerTypes.SCRATCH, ProjectContainerTypes.CONTAINER,
      ProjectTypes.HelmChart
    ];
    const projectType = getProjectTypeData(projectDetails, true);
    if (RHCertProjectType.indexOf(projectType) !== -1) {
      isEnabled = true;
    }
    return isEnabled;
  };
  const isRHCERTEnabled = checkIsRHCertEnabled(projectDetails) || false;
  const isPartnerInfoEnabled = isRHCERTEnabled || projectDetails?.type === ProjectTypes.openShiftVirtualization || false;
  const { isLoading: isRHCertFetching } = useProjectRHCertStatus(id, isRHCERTEnabled && !isDeprecatedType);
  const { isLoading: isSubsLoading } = usePartnerInfo(isPartnerInfoEnabled && !isDeprecatedType);
  // Used in associated product list and other places
  useAttachedProducts(
    projectDetails?.[ProjectKeys.id],
    InitialPaginationValue,
    !isEmpty(projectDetails) && !isDeprecatedType
  );
  const isFetching = (isProductInfoFetching || projectDetailsFetching || isRHCertFetching || isProjectChecklistLoading || isSubsLoading);
  const includeProjectType = () => {
    return ["Containers", "Helm Chart", "RHEL", "OpenStack-infra-container", "OpenStack-infra-noncontainer", "OpenStack-vnf", 'OpenStack-app-container', ProjectTypes.openshiftCNF,
        ProjectTypes.openShiftVirtualization].includes(projectDetails?.[ProjectKeys.type]) ||
      (projectDetails[ProjectKeys.type] === 'OpenStack' && !isEmpty(projectDetails[ProjectKeys.container]));
  }

  const redirectToLegacyRoute = () => {
    const relationID = projectDetails?.[ProjectKeys.drupal]?.[ProjectDrupalKeys.relation];
    window.location.href = `${Env.ConnectPortalUrls[Env.getEnvName()]}project/${relationID}/view`;
  }

  const renderWrapper = () => {
    if (isFetching) {
      return (<Bullseye><Spinner size="xl" /></Bullseye>)
    } else if (responseError || productIdError || projectCheckListError) {
        return  <RHCErrorPage errorStatus={responseError?.status || productIdError?.status || projectCheckListError?.status} />
    } else if (!isEmpty(projectDetails)) {
      if (isDeprecatedType) {
        return (<DeprecatedTypeRedirect />)
      } else if (includeProjectType()) {
        if (projectDetails?.[ProjectKeys.projectStatus] === "archived") {
          return (<ArchivedProjectError />)
        } else {
          return (<div>{props.children}</div>)
        }
      } else {
        redirectToLegacyRoute();
      }
    }
  }

  return (<>{renderWrapper()}</>);
};

export default CertWorkflowAppWrapper;

